import React, { useMemo, useState } from "react";
import { Card, Form, Input, Radio } from "antd";
import { BeatLoader } from "react-spinners";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { WorkOutline, Campaign, Check } from "@mui/icons-material";
import "./styles.scss";

const ROLE_OPTIONS = [
  "Investment Banking / M&A Advisory",
  "Private Equity",
  "Venture Capital",
  "Corporate Development / Strategy",
  "Founder / Operator",
  "Independent Sponsor / Search Fund",
  "Family Office / Investor",
  "Consultant / Advisor",
  "Student / Exploring",
  "Other",
];

const HEAR_OPTIONS = [
  "Google / Search",
  "LinkedIn",
  "Twitter / X",
  "Youtube",
  "ChatGPT",
  "Claude",
  "Gemini",
  "Other LLM",
  "News Article",
  "Referral",
  "Direct outreach",
  "Other",
];

const STEP_COUNT = 2;

function OnboardingPage() {
  const { http } = global.services;
  const user = useSelector((state) => state.UserReducer.profile);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [form] = Form.useForm();

  const initialValues = useMemo(
    () => ({
      onboarding_role: user?.onboarding_role ?? undefined,
      onboarding_role_other: user?.onboarding_role_other ?? "",
      onboarding_hear_about: user?.onboarding_hear_about ?? undefined,
      onboarding_hear_about_other: user?.onboarding_hear_about_other ?? "",
    }),
    [user],
  );

  const role = Form.useWatch("onboarding_role", form);
  const hear = Form.useWatch("onboarding_hear_about", form);

  const persistProfile = async (values) => {
    const payload = {
      onboarding_role: values.onboarding_role,
      onboarding_role_other:
        values.onboarding_role === "Other" ? values.onboarding_role_other : null,
      onboarding_hear_about: values.onboarding_hear_about,
      onboarding_hear_about_other:
        values.onboarding_hear_about === "Other"
          ? values.onboarding_hear_about_other
          : null,
    };

    await http.put("user-profile", payload);

    const returnTo = sessionStorage.getItem("postOnboardingRedirect") || "/";
    sessionStorage.removeItem("postOnboardingRedirect");
    window.location.href = returnTo;
  };

  const handleNext = async () => {
    try {
      const roleVal = form.getFieldValue("onboarding_role");
      const fields = ["onboarding_role"];
      if (roleVal === "Other") fields.push("onboarding_role_other");
      await form.validateFields(fields);
      setStep(1);
    } catch {
      /* validation messages shown on fields */
    }
  };

  const handleBack = () => setStep(0);

  const handleSubmit = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await form.validateFields();
      const values = form.getFieldsValue(true);
      await persistProfile(values);
    } catch (err) {
      if (err?.errorFields) {
        const first = err.errorFields[0];
        const name = first?.name?.[0];
        if (name === "onboarding_role" || name === "onboarding_role_other") {
          setStep(0);
        }
      } else {
        toast.error(err?.response?.data?.error ?? "Failed to save onboarding details");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="onboarding-page">
      <Card className="onboarding-card onboarding-card--shadow-brand" bordered={false}>
        <header className="onboarding-card__header onboarding-card__header--fill-brand">
          <div className="onboarding-card__header-top">
            <p className="onboarding-card__eyebrow onboarding-text-brand">
              Getting started
            </p>
            <nav
              className="onboarding-progress"
              aria-label={`Onboarding progress, step ${step + 1} of ${STEP_COUNT}`}
            >
              <ol className="onboarding-progress__steps">
                {Array.from({ length: STEP_COUNT }, (_, i) => {
                  const status =
                    i === step ? "current" : i < step ? "complete" : "pending";
                  return (
                    <li
                      key={i}
                      className="onboarding-progress__step"
                      aria-current={i === step ? "step" : undefined}
                    >
                      {i > 0 ? (
                        <span
                          className={`onboarding-progress__connector ${
                            step >= i
                              ? "onboarding-progress__connector--complete"
                              : "onboarding-progress__connector--muted"
                          }`}
                          aria-hidden
                        />
                      ) : null}
                      <span
                        className={`onboarding-progress__dot onboarding-progress__dot--${status}`}
                      >
                        {status === "complete" ? (
                          <Check className="onboarding-progress__check" aria-hidden />
                        ) : (
                          <span aria-hidden>{i + 1}</span>
                        )}
                        <span className="onboarding-progress__sr">
                          {status === "complete" && `Step ${i + 1}, completed`}
                          {status === "current" && `Step ${i + 1}, current`}
                          {status === "pending" && `Step ${i + 1}, not started`}
                        </span>
                      </span>
                    </li>
                  );
                })}
              </ol>
            </nav>
          </div>
          <h1 className="onboarding-card__title">
            {step === 0
              ? "Which best describes you?"
              : "How did you hear about us?"}
          </h1>
          <p className="onboarding-card__subtitle">
            {step === 0
              ? "Choose the option that fits best. You can go back and change your answers before submitting."
              : "Almost done — tell us how you found Conceptor."}
          </p>
        </header>

        <div className="onboarding-card__body">
          <Form
            form={form}
            layout="vertical"
            initialValues={initialValues}
            requiredMark={false}
            scrollToFirstError
          >
            <div className={step === 0 ? "onboarding-step" : "onboarding-step is-hidden"}>
              <section
                className="onboarding-section"
                aria-labelledby="onboarding-role-heading"
              >
                <div className="onboarding-section__label">
                  <span
                    className="onboarding-section__icon onboarding-section__icon--brand"
                    aria-hidden
                  >
                    <WorkOutline />
                  </span>
                  <div>
                    <h2
                      id="onboarding-role-heading"
                      className="onboarding-section__heading"
                    >
                      Which best describes you?
                    </h2>
                    <p className="onboarding-section__hint">Select one option below.</p>
                  </div>
                </div>

                <Form.Item
                  name="onboarding_role"
                  rules={[{ required: true, message: "Please select an option" }]}
                  style={{ marginBottom: role === "Other" ? 16 : 0 }}
                >
                  <Radio.Group className="onboarding-radio-grid onboarding-radio-grid--brand">
                    {ROLE_OPTIONS.map((opt) => (
                      <Radio key={opt} value={opt}>
                        {opt}
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>

                {role === "Other" ? (
                  <Form.Item
                    className="onboarding-other-field"
                    label={
                      <span style={{ fontWeight: 600, color: "#334155" }}>
                        Please specify
                      </span>
                    }
                    name="onboarding_role_other"
                    rules={[{ required: true, message: "Please tell us more" }]}
                  >
                    <Input
                      className="onboarding-field-input--brand"
                      placeholder="e.g. Lender, Board member"
                      autoComplete="organization-title"
                    />
                  </Form.Item>
                ) : null}
              </section>
            </div>

            <div className={step === 1 ? "onboarding-step" : "onboarding-step is-hidden"}>
              <section
                className="onboarding-section"
                aria-labelledby="onboarding-hear-heading"
              >
                <div className="onboarding-section__label">
                  <span
                    className="onboarding-section__icon onboarding-section__icon--brand"
                    aria-hidden
                  >
                    <Campaign />
                  </span>
                  <div>
                    <h2
                      id="onboarding-hear-heading"
                      className="onboarding-section__heading"
                    >
                      How did you hear about us?
                    </h2>
                    <p className="onboarding-section__hint">Select one option below.</p>
                  </div>
                </div>

                <Form.Item
                  name="onboarding_hear_about"
                  rules={[{ required: true, message: "Please select one option" }]}
                  style={{ marginBottom: hear === "Other" ? 16 : 0 }}
                >
                  <Radio.Group className="onboarding-radio-grid onboarding-radio-grid--brand">
                    {HEAR_OPTIONS.map((opt) => (
                      <Radio key={opt} value={opt}>
                        {opt}
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>

                {hear === "Other" ? (
                  <Form.Item
                    className="onboarding-other-field"
                    label={
                      <span style={{ fontWeight: 600, color: "#334155" }}>
                        Tell us more
                      </span>
                    }
                    name="onboarding_hear_about_other"
                    rules={[
                      {
                        required: true,
                        message: "Please tell us where you heard about us",
                      },
                    ]}
                  >
                    <Input
                      className="onboarding-field-input--brand"
                      placeholder="e.g. Podcast name, article, or referral"
                    />
                  </Form.Item>
                ) : null}
              </section>
            </div>

            <div
              className={`onboarding-actions ${step === 0 ? "onboarding-actions--end" : ""}`}
            >
              {step === 1 && (
                <button type="button" className="back-btn" onClick={handleBack}>
                  Back
                </button>
              )}
              <button
                type="button"
                className="submit-btn"
                disabled={loading}
                onClick={step === 0 ? handleNext : handleSubmit}
              >
                {loading ? (
                  <BeatLoader size={9} color="#ffffff" />
                ) : step === 0 ? (
                  "Next"
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          </Form>
        </div>
      </Card>
    </div>
  );
}

export default OnboardingPage;
